import { Link } from 'react-router-dom';
import { interpretationOfData } from './interpretation-of-data';
import { experimentalDesign } from './experimental-design';
import { identifyingVariables } from './identifying-variables';
import { evaluationOfModels } from './evaluation-of-models';

export default function ACTSciencePage() {
  const scientificInvestigation = [
    experimentalDesign,
    identifyingVariables
  ];

  const allTopics = [
    ...interpretationOfData.skills,
    ...scientificInvestigation,
    ...evaluationOfModels
  ];

  const totalPassages = allTopics.reduce((sum, topic) => sum + topic.passages.length, 0);

  const domains = [
    {
      name: 'Interpretation of Data',
      percentage: '40-50%',
      description: 'Analyze scientific data in tables, graphs, and diagrams. Recognize trends, translate data between formats, and perform interpolation or extrapolation.',
      topics: interpretationOfData.skills,
      color: 'from-blue-500 to-cyan-500',
      icon: 'ri-line-chart-line'
    },
    {
      name: 'Scientific Investigation',
      percentage: '20-30%',
      description: 'Understand experimental design and execution. Identify variables, understand procedures, and evaluate experimental tools.',
      topics: scientificInvestigation,
      color: 'from-purple-500 to-pink-500',
      icon: 'ri-flask-line'
    },
    {
      name: 'Evaluation of Models, Inferences & Results',
      percentage: '25-35%',
      description: 'Judge the validity of scientific information and draw conclusions. Compare conflicting viewpoints and evaluate hypotheses.',
      topics: evaluationOfModels,
      color: 'from-emerald-500 to-teal-500',
      icon: 'ri-scales-3-line'
    }
  ];

  const contentAreas = [
    { name: 'Biology', icon: 'ri-leaf-line', topics: 'Cell biology, DNA/RNA, natural selection, ecology' },
    { name: 'Chemistry', icon: 'ri-test-tube-line', topics: 'pH scale, phase changes, solubility, molar mass' },
    { name: 'Physics', icon: 'ri-rocket-line', topics: 'Forces, motion, gravity, density, energy conservation' },
    { name: 'Earth/Space', icon: 'ri-earth-line', topics: 'Geology, astronomy, meteorology, oceanography' }
  ];

  const passageFormats = [
    {
      name: 'Data Representation',
      count: '2-3 passages',
      description: 'Focuses on graphs and tables; requires pure data analysis',
      icon: 'ri-bar-chart-box-line'
    },
    {
      name: 'Research Summaries',
      count: '2-3 passages',
      description: 'Describes experiments; focuses on design and results',
      icon: 'ri-microscope-line'
    },
    {
      name: 'Conflicting Viewpoints',
      count: '1 passage',
      description: 'Presents competing hypotheses; requires comparing perspectives',
      icon: 'ri-git-branch-line'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <i className="ri-flask-line text-4xl"></i>
            <h1 className="text-5xl font-bold">ACT Science</h1>
          </div>
          <p className="text-xl text-blue-100 max-w-3xl">
            Master scientific reasoning and analytical skills with {totalPassages}+ practice passages covering all ACT Science question types and content areas.
          </p>
          <div className="flex flex-wrap gap-4 mt-6">
            <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <div className="text-2xl font-bold">{allTopics.length}</div>
              <div className="text-sm text-blue-100">Skills Covered</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <div className="text-2xl font-bold">{totalPassages}+</div>
              <div className="text-sm text-blue-100">Practice Passages</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <div className="text-2xl font-bold">40</div>
              <div className="text-sm text-blue-100">Questions</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Quick Links */}
        <div className="grid md:grid-cols-3 gap-4 mb-12">
          <Link to="/act-science-worksheets" className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all group">
            <i className="ri-file-list-3-line text-3xl text-blue-600 mb-3"></i>
            <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600 transition-colors">Browse Worksheets</h3>
            <p className="text-sm text-gray-600">Download topic-specific practice drills</p>
          </Link>
          <Link to="/act-science-study-plan" className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all group">
            <i className="ri-calendar-check-line text-3xl text-purple-600 mb-3"></i>
            <h3 className="font-semibold text-lg mb-2 group-hover:text-purple-600 transition-colors">Study Plan</h3>
            <p className="text-sm text-gray-600">Weekly prep schedule to test day</p>
          </Link>
          <Link to="/act-science-progress" className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all group">
            <i className="ri-trophy-line text-3xl text-emerald-600 mb-3"></i>
            <h3 className="font-semibold text-lg mb-2 group-hover:text-emerald-600 transition-colors">Track Progress</h3>
            <p className="text-sm text-gray-600">Monitor your mastery by skill</p>
          </Link>
        </div>

        {/* Test Overview */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold mb-4">About the ACT Science Test</h2>
          <p className="text-gray-700 mb-6 leading-relaxed">
            The ACT Science section measures your <strong>scientific reasoning and analytical skills</strong> rather than your ability to memorize specific facts. It tests how well you can interpret, analyze, and evaluate scientific information presented in various formats.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <i className="ri-time-line text-blue-600"></i>
                Test Format
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                  <span><strong>40 questions</strong> in 35 minutes</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                  <span><strong>6-7 passages</strong> with 5-7 questions each</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                  <span><strong>53 seconds</strong> per question average</span>
                </li>
              </ul>
            </div>
            <div className="bg-emerald-50 p-6 rounded-xl">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <i className="ri-lightbulb-line text-emerald-600"></i>
                Key Strategy
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-circle-fill text-emerald-600 mt-1"></i>
                  <span>Read questions <strong>before</strong> the passage</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-circle-fill text-emerald-600 mt-1"></i>
                  <span>Focus on <strong>data interpretation</strong>, not memorization</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-circle-fill text-emerald-600 mt-1"></i>
                  <span>Practice with <strong>authentic passages</strong></span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Passage Formats */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Passage Formats</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {passageFormats.map((format) => (
              <div key={format.name} className="bg-white p-6 rounded-xl shadow-sm border-2 border-gray-100">
                <i className={`${format.icon} text-4xl text-blue-600 mb-4`}></i>
                <h3 className="font-bold text-lg mb-2">{format.name}</h3>
                <div className="text-sm text-blue-600 font-semibold mb-3">{format.count}</div>
                <p className="text-gray-600 text-sm">{format.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Content Areas */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Content Areas</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contentAreas.map((area) => (
              <div key={area.name} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all">
                <i className={`${area.icon} text-4xl text-cyan-600 mb-4`}></i>
                <h3 className="font-bold text-lg mb-2">{area.name}</h3>
                <p className="text-sm text-gray-600">{area.topics}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Core Skill Domains */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Core Skill Domains</h2>
          <div className="space-y-8">
            {domains.map((domain) => (
              <div key={domain.name} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className={`bg-gradient-to-r ${domain.color} text-white p-6`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <i className={`${domain.icon} text-3xl`}></i>
                      <h3 className="text-2xl font-bold">{domain.name}</h3>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                      <div className="text-sm font-semibold">{domain.percentage}</div>
                    </div>
                  </div>
                  <p className="text-white/90">{domain.description}</p>
                </div>
                <div className="p-6">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {domain.topics.map((topic) => (
                      <div key={topic.id} className="border-2 border-gray-100 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all group">
                        <h4 className="font-semibold mb-2 group-hover:text-blue-600 transition-colors">{topic.title}</h4>
                        <p className="text-sm text-gray-600 mb-3">{topic.description}</p>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">{topic.passages.length} passages</span>
                          <button className="text-blue-600 font-semibold hover:text-blue-700 whitespace-nowrap">
                            Practice Now →
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Master ACT Science?</h2>
          <p className="text-xl text-blue-100 mb-6 max-w-2xl mx-auto">
            Start practicing with authentic passages covering all question types and content areas.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/act-science-worksheets" className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap">
              Download Worksheets
            </Link>
            <Link to="/act-science-study-plan" className="bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap">
              View Study Plan
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}